/**
 * Telegram connect via the unified engine. Loads the official Telegram Login
 * Widget for the workspace's configured bot (botUsername comes from the External
 * APIs config, not env), then completes the link through `/me/connections` so the
 * bot token is resolved from the per-workspace config (config → env chain).
 *
 * The widget container is tracked via a callback ref (held in state) so the
 * effect runs exactly when the node is mounted — appending Telegram's <script>
 * to a live, stable node and avoiding the portal/StrictMode timing races that
 * leave the button unrendered.
 *
 * UX: a loader shows while the widget initializes; once Telegram injects its
 * button the loader clears; if the button never appears (usually the bot's
 * `/setdomain` doesn't match this site) we surface an actionable error.
 */

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { meConnectionsService } from "@/services/me-connections-service";

const TELEGRAM_WIDGET_SRC = "https://telegram.org/js/telegram-widget.js?22";
/** If Telegram hasn't injected its iframe button by this long, something's wrong. */
const WIDGET_TIMEOUT_MS = 8000;
const CALLBACK_NAME = "onTaqatTelegramAuth";

interface ConnectTelegramDialogProps {
  /** Bot @username (without @) from the connection config. */
  botUsername?: string;
  onClose: () => void;
  onConnected: () => void;
}

type Phase = "loading" | "ready" | "verifying" | "error";

export function ConnectTelegramDialog({
  botUsername,
  onClose,
  onConnected,
}: ConnectTelegramDialogProps) {
  // The widget container, set by a callback ref → the effect runs once it mounts.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  // Keep the latest onConnected without making it an effect dependency.
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;

  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!botUsername) {
      setError("Telegram isn't configured — add a bot username in Settings → External APIs.");
      setPhase("error");
      return;
    }
    if (!container) return;

    setPhase((p) => (p === "error" ? "loading" : p));
    setError(null);

    // The global Telegram invokes with the signed payload (reads latest handler via ref).
    (window as unknown as Record<string, unknown>)[CALLBACK_NAME] = async (
      payload: Record<string, unknown>,
    ) => {
      setPhase("verifying");
      setError(null);
      try {
        const res = await meConnectionsService.complete("telegram", { payload });
        if (!res.success) throw new Error("Could not complete the Telegram link.");
        toast.success("Telegram connected");
        onConnectedRef.current();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verification failed");
        setPhase("error");
      }
    };

    // Append the widget script to this live node if not already present.
    if (
      !container.querySelector("script[data-telegram-login]") &&
      !container.querySelector("iframe")
    ) {
      const script = document.createElement("script");
      script.async = true;
      script.src = TELEGRAM_WIDGET_SRC;
      script.setAttribute("data-telegram-login", botUsername);
      script.setAttribute("data-size", "large");
      script.setAttribute("data-userpic", "true");
      script.setAttribute("data-request-access", "write");
      script.setAttribute("data-onauth", `${CALLBACK_NAME}(user)`);
      container.appendChild(script);
    }

    if (container.querySelector("iframe")) setPhase("ready");

    // Telegram swaps the <script> for an <iframe> button once loaded.
    const observer = new MutationObserver(() => {
      if (container.querySelector("iframe")) {
        setPhase((p) => (p === "loading" ? "ready" : p));
      }
    });
    observer.observe(container, { childList: true, subtree: true });

    const timeout = window.setTimeout(() => {
      if (!container.querySelector("iframe")) {
        setError(
          `Couldn't load the Telegram button. Make sure the bot @${botUsername}'s domain (BotFather → /setdomain) matches ${window.location.host}.`,
        );
        setPhase((p) => (p === "loading" ? "error" : p));
      }
    }, WIDGET_TIMEOUT_MS);

    return () => {
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, [container, botUsername]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Connect Telegram</DialogTitle>
          <DialogDescription>
            Authenticate with Telegram so messages from this account route to your
            TeamMate user. Telegram returns a signed payload we verify server-side.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[64px] space-y-3 py-2">
          {/* Telegram injects its button into this node once loaded. */}
          <div ref={setContainer} className="flex justify-center" />

          {phase === "loading" ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading the Telegram button…
            </div>
          ) : null}

          {phase === "verifying" ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Verifying your account…
            </div>
          ) : null}

          {phase === "error" && error ? (
            <p className="text-center text-sm text-destructive">{error}</p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
